import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useCallback } from 'react';
import { Grid, Typography, TextField, Alert, RadioGroup, Radio, FormControlLabel, Icon, Spacer, useTheme } from '@emburse/embark-core';
import { debounce } from 'lodash';
import { AuthCard, CustomStepper, InputPassword } from '../../components';
import VerticallyCenteredLayout from '../../components/Layouts/VerticallyCenteredLayout';
import { ratePassword } from '../../util/ratePassword';
import { isEmburseProfessional } from '../../util/helpers';
import { $t, useTranslation } from '../../hooks/useTranslation';
var WizardStep;
(function (WizardStep) {
    WizardStep[WizardStep["EnterEmail"] = 1] = "EnterEmail";
    WizardStep[WizardStep["ChooseDeliveryMethod"] = 2] = "ChooseDeliveryMethod";
    WizardStep[WizardStep["EnterVerificationCode"] = 3] = "EnterVerificationCode";
    WizardStep[WizardStep["ResetPassword"] = 4] = "ResetPassword";
    WizardStep[WizardStep["ValidateNewPassword"] = 5] = "ValidateNewPassword";
})(WizardStep || (WizardStep = {}));
export default function PasswordAssistance({ CurrentStep, Email, SendTo, Phone, Validation, MinimumPasswordRating }) {
    useTranslation();
    const theme = useTheme();
    const [sixChar, setSixChar] = useState(false);
    const [oneNum, setOneNum] = useState(false);
    const [eightChar, setEightChar] = useState(false);
    const [oneUpper, setOneUpper] = useState(false);
    const [oneSpecial, setOneSpecial] = useState(false);
    const [oneLower, setOneLower] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [passwordRating, setPasswordRating] = useState(0);
    const applicationLabel = isEmburseProfessional() ? '' : 'Certify';
    const applicationLabelWithWhitespace = ` ${applicationLabel} `;
    useEffect(() => {
        setPasswordRating(ratePassword(newPassword));
        setSixChar(newPassword.length > 5);
        setOneNum(/\d/.test(newPassword));
        setEightChar(newPassword.length > 7);
        setOneUpper(/[A-Z]/.test(newPassword));
        setOneSpecial(/[!@#$%^&*()_+=,./<>?;:]/.test(newPassword));
        setOneLower(/[a-z]/.test(newPassword));
    }, [newPassword]);
    const passwordRequirementValues = [sixChar, oneNum, eightChar, oneUpper, oneSpecial, oneLower];
    const customStepperStyles = {
        maxWidth: '335px',
        margin: '0 auto'
    };
    const debouncedSetNewPassword = debounce((e) => {
        setNewPassword(e.target.value);
    }, 50);
    const setPasswordRequirements = useCallback(debouncedSetNewPassword, [debouncedSetNewPassword]);
    const getPassRequirementColor = (index) => passwordRequirementValues[index] ? theme.palette.success.main : theme.palette.text.disabled;
    const passwordRequirements = [
        $t('6 characters (required)'),
        $t('One number'),
        $t('8 characters'),
        $t('One uppercase character'),
        $t('One special character'),
        $t('One lowercase character')
    ];
    const isResetPasswordStep = () => CurrentStep === WizardStep.ResetPassword;
    const isFromWelcomeEmail = () => window.location.search.includes('RESET=1');
    const minimumPasswordRatingValue = () => {
        switch (MinimumPasswordRating) {
            case 'Better':
                return 3;
            case 'Strong':
                return 4;
            case 'Stronger':
                return 5;
            case 'Strongest':
                return 6;
            default:
                return null;
        }
    };
    const getPassStrengthText = () => {
        if (!sixChar) {
            return 'Too Weak';
        }
        switch (passwordRating) {
            case 0:
            case 1:
            case 2:
                return 'Weak';
            case 3:
                return 'Better';
            case 4:
                return 'Strong';
            case 5:
                return 'Stronger';
            case 6:
                return 'Strongest';
            default:
                return '';
        }
    };
    const isAllRequirementsMet = () => passwordRating >= minimumPasswordRatingValue();
    const getPassStrengthColor = () => {
        if (isAllRequirementsMet()) {
            switch (passwordRating) {
                case 0:
                    return theme.palette.text.disabled;
                case 1:
                case 2:
                    return theme.palette.error.main;
                case 3:
                case 4:
                    return theme.palette.warning.main;
                case 5:
                case 6:
                    return theme.palette.success.main;
                default:
            }
        }
        if (newPassword.length > 0) {
            return theme.palette.error.main;
        }
        return theme.palette.text.disabled;
    };
    const debouncedSetConfirmPassword = debounce((e) => {
        setConfirmPassword(e.target.value);
    }, 50);
    const handleConfirmPassword = useCallback(debouncedSetConfirmPassword, [
        debouncedSetConfirmPassword
    ]);
    const isPasswordMatch = () => confirmPassword === newPassword;
    const getSteps = () => ['1', '2', '3', '4'];
    const isWizardNextButtonDisabled = () => isResetPasswordStep() && (!isAllRequirementsMet() || !isPasswordMatch());
    return (_jsx(VerticallyCenteredLayout, { children: _jsx(AuthCard, { id: "PasswordAssistance", titleVariant: "h5", titleText: !isResetPasswordStep() ? $t('Password Assistance') : $t('Create New Password'), buttonLabel: !isResetPasswordStep() ? $t('Continue') : $t('Continue To Login'), disabled: isWizardNextButtonDisabled(), children: _jsxs(Grid, { container: true, spacing: 3, direction: "column", justifyContent: "center", alignItems: "stretch", children: [_jsx(Grid, { item: true, style: { marginTop: '-40px' }, children: _jsx(CustomStepper, { currentStep: CurrentStep - 1, getHorizontalSteps: getSteps, style: customStepperStyles }) }), Validation.length > 0 && (_jsx(Grid, { item: true, children: _jsx(Alert, { severity: "error", children: Validation[0] }) })), !isResetPasswordStep() ? (_jsxs(_Fragment, { children: [_jsxs(Grid, { item: true, children: [CurrentStep === WizardStep.EnterEmail && (_jsxs(Typography, { variant: "body1", children: [_jsx("strong", { children: isFromWelcomeEmail()
                                                    ? $t(`Create your first${applicationLabelWithWhitespace}password!`)
                                                    : $t('Help us help you!') }), _jsx("br", {}), isFromWelcomeEmail()
                                                ? $t(`To set a password, please enter the email address associated with 
                                                        your${applicationLabelWithWhitespace}account.`)
                                                : $t(`To reset your password, please enter the email address 
                                                    associated with your${applicationLabelWithWhitespace}account.`)] })), CurrentStep === WizardStep.ChooseDeliveryMethod && (_jsxs(Typography, { variant: "body1", children: [$t("To verify your account we'll send you a verification code."), _jsx("br", {}), $t('How would you like to receive the code?')] })), CurrentStep === WizardStep.EnterVerificationCode && (_jsxs(Typography, { variant: "body1", children: [`A verification code has been sent by ${SendTo.toLowerCase() === 'phone'
                                                ? 'text message'
                                                : 'email'} to `, _jsxs("strong", { children: [SendTo.toLowerCase() === 'phone' ? Phone : Email, "."] }), _jsx("br", {}), $t('Enter the code below to verify account ownership.')] }))] }), _jsxs(Grid, { item: true, children: [CurrentStep === WizardStep.EnterEmail && (_jsx(TextField, { id: "Username", name: "Username", fullWidth: true, label: $t('Email'), type: "email" })), CurrentStep === WizardStep.ChooseDeliveryMethod && (_jsxs(RadioGroup, { name: "SendTo", defaultValue: "Email", children: [_jsx(FormControlLabel, { value: "1", control: _jsx(Radio, { color: "primary", name: "SendTo", value: "Email" }), label: `${$t('Email verification code to:')} ${Email}` }), Phone && (_jsx(FormControlLabel, { value: "0", control: _jsx(Radio, { color: "primary", name: "SendTo", value: "Phone" }), label: `${$t('Text verification code to:')} ${Phone}` }))] })), CurrentStep === WizardStep.EnterVerificationCode && (_jsx(TextField, { id: "VerificationCode", name: "VerificationCode", label: $t('Verification Code'), type: "text", fullWidth: true }))] }), _jsx(Grid, { item: true, children: _jsxs(Typography, { variant: "body2", children: [(CurrentStep === 1 || CurrentStep === 2) &&
                                            $t(`If you can't remember or can't access the email associated with your${applicationLabelWithWhitespace}account, 
                                    contact your internal system administrator.`), CurrentStep === 3 &&
                                            $t('Keep in mind this code will expire in 30 minutes.')] }) }), CurrentStep === 3 && SendTo === 'Email' && (_jsx(Grid, { item: true, children: _jsx(Typography, { variant: "body2", style: { textAlign: 'left' }, children: $t(`If you don't see your code, please make sure to check spam folders,
                                                 and that your email address is registered ${isEmburseProfessional && 'with'} ${applicationLabel}. 
                                                 If you still don't see your code, contact your company administrator.`) }) }))] })) : (_jsxs(_Fragment, { children: [_jsxs(Grid, { item: true, children: [_jsx(Icon, { isLigature: true, fontIcon: "lock", style: { color: getPassStrengthColor(), verticalAlign: 'top' } }), _jsx(Spacer, { direction: "horizontal", size: 8 }), _jsx(Typography, { variant: "body1", display: "inline", style: { color: getPassStrengthColor() }, children: getPassStrengthText() }), _jsx("br", {}), _jsx(Typography, { variant: "body2", children: `${$t('Your password must contain at least')} ${minimumPasswordRatingValue() || 3} ${$t('of the following:')}` })] }), _jsx(Grid, { item: true, children: _jsx(Grid, { container: true, spacing: 1, direction: "row", children: passwordRequirements.map((item, index) => (_jsxs(Grid, { item: true, xs: 6, direction: "row", style: { display: 'flex', alignItems: 'center' }, children: [_jsx(Icon, { isLigature: true, fontIcon: passwordRequirementValues[index]
                                                    ? 'check'
                                                    : 'remove', style: { color: getPassRequirementColor(index) } }), _jsx(Spacer, { direction: "horizontal", size: 5 }), _jsx(Typography, { variant: "body2", display: "inline", style: { color: getPassRequirementColor(index) }, children: item })] }, item))) }) }), _jsx(Grid, { item: true, children: _jsx(InputPassword, { id: "NewPassword", name: "NewPassword", label: $t('New Password'), onChangeHandler: setPasswordRequirements }) }), _jsxs(Grid, { item: true, children: [_jsx(InputPassword, { id: "ConfirmPassword", name: "ConfirmPassword", label: $t('Confirm New Password'), onChangeHandler: handleConfirmPassword }), isAllRequirementsMet() && confirmPassword.length > 0 && (_jsx(Typography, { variant: "caption", style: {
                                            color: isPasswordMatch()
                                                ? theme.palette.success.main
                                                : theme.palette.error.main
                                        }, children: isPasswordMatch()
                                            ? $t('Passwords Match!')
                                            : $t('Passwords do not match.') }))] })] })), _jsx("input", { id: "pwRating", name: "pwRating", type: "hidden", value: passwordRating })] }) }) }));
}
PasswordAssistance.defaultProps = {
    MinimumPasswordRating: '',
    Phone: ''
};
